import { useState } from 'react';
import { Badge } from '@tremor/react';
import { useQueryClient } from 'react-query';
import { useToast } from '../../../../utils/useToast';
import {
  CalendarOption,
  Connection,
  connectionsQueryKey,
  disconnect,
  errorMessage,
  fetchCalendars,
  PROVIDER_NAMES,
  startConnect,
  updateConnection,
  useConnections,
} from './api';

const CALENDARS: Array<{ provider: 'google-calendar' | 'outlook-calendar'; blurb: string }> = [
  {
    provider: 'google-calendar',
    blurb: 'Adds your approved leave to your Google Calendar as busy, all-day events.',
  },
  {
    provider: 'outlook-calendar',
    blurb: 'Adds your approved leave to your Outlook calendar as out-of-office.',
  },
];

export function ConnectionStatus({ connection }: { connection?: Connection }) {
  if (!connection) return <Badge color="gray">Not connected</Badge>;
  if (connection.status === 'needs_reconnect') {
    return <Badge color="amber">Needs reconnect</Badge>;
  }
  return <Badge color="green">Connected</Badge>;
}

function CalendarPicker({ connection }: { connection: Connection }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [calendars, setCalendars] = useState<CalendarOption[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setCalendars(await fetchCalendars(connection.id));
    } catch (err) {
      toast({
        title: 'Could not load calendars',
        description: errorMessage(err, 'Try again later.'),
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const choose = async (calendarId: string) => {
    try {
      await updateConnection(connection.id, { calendarId });
      toast({ title: 'Saved', description: 'Leave will go to that calendar.', variant: 'success' });
      await queryClient.invalidateQueries(connectionsQueryKey);
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err, 'Try again.'), variant: 'error' });
    }
  };

  if (!calendars) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-red-600 hover:underline disabled:opacity-60"
        onClick={load}
        disabled={loading}
      >
        {loading ? 'Loading calendars…' : 'Choose calendar'}
      </button>
    );
  }

  return (
    <label className="flex items-center gap-2 text-xs text-gray-600">
      Add leave to
      <select
        aria-label={`${PROVIDER_NAMES[connection.provider]} target calendar`}
        className="rounded-md border-gray-300 py-1 text-xs"
        value={connection.settings.calendarId || calendars.find((c) => c.primary)?.id || 'primary'}
        onChange={(event) => choose(event.target.value)}
      >
        {calendars.map((calendar) => (
          <option key={calendar.id} value={calendar.id}>
            {calendar.name}
            {calendar.primary ? ' (primary)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The signed-in user's own calendar connections. Shown on the Profile page
 * (every role) and in Settings → Integrations (admins).
 */
export function CalendarConnections({ returnTo }: { returnTo: 'settings' | 'profile' }) {
  const { data, isLoading, error } = useConnections();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-gray-500">Loading calendar connections…</p>;
  if (error || !data) {
    return <p className="text-sm text-gray-500">Could not load calendar connections.</p>;
  }

  const connect = async (provider: 'google-calendar' | 'outlook-calendar') => {
    setPending(provider);
    try {
      await startConnect(provider, returnTo);
    } catch (err) {
      setPending(null);
      toast({
        title: 'Could not start the connection',
        description: errorMessage(err, 'Unable to reach the calendar provider.'),
        variant: 'error',
      });
    }
  };

  const remove = async (connection: Connection) => {
    setPending(connection.id);
    try {
      await disconnect(connection.id);
      toast({
        title: 'Disconnected',
        description: `${PROVIDER_NAMES[connection.provider]} is disconnected and its Redstone events removed.`,
        variant: 'success',
      });
      await queryClient.invalidateQueries(connectionsQueryKey);
    } catch (err) {
      toast({ title: 'Disconnect failed', description: errorMessage(err, 'Try again.'), variant: 'error' });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-4">
      {CALENDARS.map(({ provider, blurb }) => {
        const connection = data.connections.find((c) => c.provider === provider && c.own);
        const available = data.available?.[provider] !== false;
        return (
          <div
            key={provider}
            data-testid={`calendar-${provider}`}
            className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-start md:justify-between"
          >
            <div>
              <h5 className="text-sm font-medium text-gray-900">{PROVIDER_NAMES[provider]}</h5>
              <p className="mt-1 text-sm text-gray-500">{blurb}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <ConnectionStatus connection={connection} />
                {connection?.accountEmail && <span>{connection.accountEmail}</span>}
                {connection?.status === 'needs_reconnect' && (
                  <span>Access was revoked or expired. Reconnect to keep syncing.</span>
                )}
                {!available && <span>Not set up on this server yet.</span>}
              </div>
              {connection?.status === 'active' && (
                <div className="mt-2">
                  <CalendarPicker connection={connection} />
                </div>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              {(!connection || connection.status === 'needs_reconnect') && (
                <button
                  type="button"
                  className="rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={() => connect(provider)}
                  disabled={pending !== null || !available}
                >
                  {connection ? 'Reconnect' : 'Connect'}
                </button>
              )}
              {connection && (
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
                  onClick={() => remove(connection)}
                  disabled={pending !== null}
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
